import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Avatar,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useLanguage } from '../App';

// Sites translations
const sitesTranslations = {
  en: {
    sites_title: 'Booking Sites',
    new_site: 'New Site',
    site_name: 'Site Name',
    site_url: 'Site URL',
    color: 'Color',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    no_sites: 'No booking sites found',
    add_site: 'Add Booking Site',
    edit_site: 'Edit Booking Site',
    save: 'Save',
    cancel: 'Cancel',
    delete_confirmation: 'Delete Site',
    delete_message: 'Are you sure you want to delete this booking site?',
    site_saved: 'Booking site saved successfully!',
    site_deleted: 'Booking site deleted successfully!',
    error_saving: 'Error saving booking site',
    error_deleting: 'Error deleting booking site',
    error_loading: 'Error loading booking sites'
  },
  pt: {
    sites_title: 'Sites de Reserva',
    new_site: 'Novo Site',
    site_name: 'Nome do Site',
    site_url: 'URL do Site',
    color: 'Cor',
    actions: 'Ações',
    edit: 'Editar',
    delete: 'Excluir',
    no_sites: 'Nenhum site de reserva encontrado',
    add_site: 'Adicionar Site de Reserva',
    edit_site: 'Editar Site de Reserva',
    save: 'Salvar',
    cancel: 'Cancelar',
    delete_confirmation: 'Excluir Site',
    delete_message: 'Tem certeza que deseja excluir este site de reserva?',
    site_saved: 'Site de reserva salvo com sucesso!',
    site_deleted: 'Site de reserva excluído com sucesso!',
    error_saving: 'Erro ao salvar site de reserva',
    error_deleting: 'Erro ao excluir site de reserva',
    error_loading: 'Erro ao carregar sites de reserva'
  },
  es: {
    sites_title: 'Sitios de Reserva',
    new_site: 'Nuevo Sitio',
    site_name: 'Nombre del Sitio',
    site_url: 'URL del Sitio',
    color: 'Color',
    actions: 'Acciones',
    edit: 'Editar',
    delete: 'Eliminar',
    no_sites: 'No se encontraron sitios de reserva',
    add_site: 'Agregar Sitio de Reserva',
    edit_site: 'Editar Sitio de Reserva',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete_confirmation: 'Eliminar Sitio',
    delete_message: '¿Está seguro que desea eliminar este sitio de reserva?',
    site_saved: '¡Sitio de reserva guardado exitosamente!',
    site_deleted: '¡Sitio de reserva eliminado exitosamente!',
    error_saving: 'Error al guardar sitio de reserva',
    error_deleting: 'Error al eliminar sitio de reserva',
    error_loading: 'Error al cargar sitios de reserva'
  }
};

function SitesList() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, site: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, site: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { language } = useLanguage();

  const t = (key) => {
    return sitesTranslations[language]?.[key] || sitesTranslations.en[key] || key;
  };

  // Mock data for now - replace with actual API calls
  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      setLoading(true);
      
      // Load from localStorage first
      const savedSites = localStorage.getItem('sites');
      if (savedSites) {
        setSites(JSON.parse(savedSites));
        setLoading(false);
        return;
      }
      
      // If no saved sites, create default ones
      const mockSites = [
        {
          id: '1',
          name: 'Airbnb',
          url: 'https://airbnb.com',
          color: '#FF5A5F'
        },
        {
          id: '2',
          name: 'Booking.com',
          url: 'https://booking.com',
          color: '#003580'
        },
        {
          id: '3',
          name: 'VRBO',
          url: 'https://vrbo.com',
          color: '#0066CC'
        },
        {
          id: '4',
          name: 'Expedia',
          url: 'https://expedia.com',
          color: '#FFC72C'
        }
      ];
      
      // Save default sites to localStorage
      localStorage.setItem('sites', JSON.stringify(mockSites));
      setSites(mockSites);
      setLoading(false);
    } catch (error) {
      setSnackbar({ open: true, message: t('error_loading'), severity: 'error' });
      setLoading(false);
    }
  };

  const handleSave = async (siteData) => {
    try {
      let updatedSites;
      
      if (editDialog.site) {
        // Update existing site
        updatedSites = sites.map(s => 
          s.id === editDialog.site.id ? { ...siteData, id: editDialog.site.id } : s
        );
      } else {
        // Add new site
        const newSite = { ...siteData, id: Date.now().toString() };
        updatedSites = [...sites, newSite];
      }
      
      // Save to localStorage
      localStorage.setItem('sites', JSON.stringify(updatedSites));
      setSites(updatedSites);
      
      setEditDialog({ open: false, site: null });
      setSnackbar({ open: true, message: t('site_saved'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('error_saving'), severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      const updatedSites = sites.filter(s => s.id !== deleteDialog.site.id);
      
      // Save to localStorage
      localStorage.setItem('sites', JSON.stringify(updatedSites));
      setSites(updatedSites);
      
      setDeleteDialog({ open: false, site: null });
      setSnackbar({ open: true, message: t('site_deleted'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('error_deleting'), severity: 'error' });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('sites_title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditDialog({ open: true, site: null })}
        >
          {t('new_site')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('site_name')}</TableCell>
              <TableCell>{t('site_url')}</TableCell>
              <TableCell>{t('color')}</TableCell>
              <TableCell align="center">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="textSecondary">
                    {t('no_sites')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sites.map((site) => (
                <TableRow key={site.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: site.color, width: 32, height: 32 }}>
                        <LanguageIcon />
                      </Avatar>
                      <Typography variant="body1" fontWeight="bold">
                        {site.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="primary" component="a" href={site.url} target="_blank">
                      {site.url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={site.color}
                      sx={{ 
                        bgcolor: site.color, 
                        color: 'white',
                        fontFamily: 'monospace'
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => setEditDialog({ open: true, site })}
                      title={t('edit')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, site })}
                      title={t('delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Site Dialog */}
      <SiteDialog
        open={editDialog.open}
        site={editDialog.site}
        onClose={() => setEditDialog({ open: false, site: null })}
        onSave={handleSave}
        t={t}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, site: null })}
      >
        <DialogTitle>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('delete_message')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>{deleteDialog.site?.name}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, site: null })}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Site Edit/Add Dialog Component
function SiteDialog({ open, site, onClose, onSave, t }) {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    color: '#FF5A5F'
  });

  useEffect(() => {
    if (site) {
      setFormData(site);
    } else {
      setFormData({
        name: '',
        url: '',
        color: '#FF5A5F'
      });
    }
  }, [site]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {site ? t('edit_site') : t('add_site')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('site_name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('site_url')}
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://example.com"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label={t('color')}
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                placeholder="#FF5A5F"
                sx={{ flexGrow: 1 }}
              />
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: formData.color,
                  border: '1px solid #ccc',
                  borderRadius: 1
                }}
              />
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                style={{ width: 40, height: 40, border: 'none', borderRadius: 4 }}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!formData.name || !formData.url}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SitesList;
